import { useContext } from 'react';
import { StyleSheet, View, type ViewStyle } from 'react-native';

import { colors, darkColors, P } from '~/common/styleguide';
import CustomAppearanceContext from '~/context/CustomAppearanceContext';
import { type Query } from '~/types';

import { ClearButton } from './ClearButton';
import {
  FILTER_COMPATIBILITY,
  FILTER_MODULE_TYPE,
  FILTER_PLATFORMS,
  FILTER_REQUIRES_MAIN_SEARCH,
  FILTER_STATUS,
  FILTER_TYPE,
} from './helpers';
import { Button } from '../Button';
import { Filter as FilterIcon } from '../Icons';

type FilterButtonProps = {
  query: Query;
  onPress: () => void;
  onClearAllPress: () => void;
  isFilterVisible: boolean;
  containerStyle?: ViewStyle;
  style?: ViewStyle;
};

export function FilterButton({
  isFilterVisible,
  query,
  onPress,
  onClearAllPress,
  containerStyle,
  style,
}: FilterButtonProps) {
  const { isDark } = useContext(CustomAppearanceContext);

  const params = [
    ...FILTER_PLATFORMS.map(platform => platform.param),
    ...FILTER_REQUIRES_MAIN_SEARCH.map(filter => filter.param),
    ...FILTER_STATUS.map(status => status.param),
    ...FILTER_COMPATIBILITY.map(compatibility => compatibility.param),
    ...FILTER_TYPE.map(entryType => entryType.param),
    ...FILTER_MODULE_TYPE.map(moduleType => moduleType.param),
  ];

  const filterCount = Object.keys(query).reduce(
    (acc, q) => (params.includes(q as keyof Query) ? acc + 1 : acc),
    0
  );
  const isFilterCount = !!filterCount;

  const backgroundColor = isDark ? darkColors.border : colors.gray5;
  const borderLeftColor = isDark ? darkColors.dark : colors.gray6;

  return (
    <View style={[styles.container, containerStyle]}>
      <Button
        onPress={onPress}
        style={[
          styles.button,
          { backgroundColor },
          isFilterVisible && styles.activeButton,
          isFilterCount && styles.leftBorderRadiusOnly,
          style,
        ]}>
        <View style={styles.displayHorizontal}>
          <View style={styles.iconContainer}>
            <FilterIcon
              fill={isFilterVisible ? colors.white : isFilterCount ? colors.primary : colors.white}
              width={14}
              height={12}
            />
          </View>
          <P style={styles.buttonText}>Filters{isFilterCount ? `: ${filterCount}` : ''}</P>
        </View>
      </Button>
      {filterCount > 0 && (
        <View style={[styles.clearButtonContainer, { backgroundColor, borderLeftColor }]}>
          <ClearButton onPress={onClearAllPress} />
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 4,
  },
  button: {
    height: '100%',
    paddingHorizontal: 8,
  },
  activeButton: {
    backgroundColor: darkColors.primaryDark,
  },
  leftBorderRadiusOnly: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },
  buttonText: {
    fontSize: 14,
    color: colors.white,
    marginLeft: 6,
    fontWeight: '500',
    userSelect: 'none',
  },
  iconContainer: {
    top: 1,
  },
  displayHorizontal: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  clearButtonContainer: {
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    borderTopRightRadius: 4,
    borderBottomRightRadius: 4,
    borderLeftWidth: 1,
  },
});
